/**
 * 把同一個產品的重複資料列併成一筆，而且不弄丟它掛的分類。
 *
 * 客戶的產品資料裡，同一個產品常常出現好幾列，於是它在清單與搜尋結果裡也跟著出現好幾次。
 * 這支在顯示之前把它們併回一筆，併的時候把每一列的分類路徑都收進 categoryPaths，
 * 所以「同時出現在多個分類底下」還是成立的。
 *
 * ⚠ **那些重複幾乎都不是為了掛多個分類，就只是資料重複。** 實際數過五份資料：
 *   2024 舊資料　239 組／682 列重複，分類真的不同的 0 組
 *   TMTS 2026　　繁中 12 組、英文 14 組，分類真的不同的 0 組
 *   AMB 2026　　 2 組，分類真的不同的 1 組
 * 267 組裡只有 1 組是真的多分類。所以去重這件事跟分類無關，是獨立需要的。
 * （一個產品要掛多個分類，現在直接在大分類那一格用分號填多個值就好，見 pathsOf。）
 *
 * 🔴 不要只留第一列、把其餘丟掉。
 * 那樣產品就只剩下第一條分類路徑，在其他分類底下會安靜地消失，
 * 畫面上不會有任何錯誤訊息，只會是「這個分類底下的東西怎麼變少了」。
 *
 * 認「同一個產品」用的是 產品名稱 ＋ 攤位號碼 ＋ 廠商名稱。
 * 前兩個是客戶信裡寫的那一組；廠商名稱是同攤位多家之後補的——
 * F0112 底下十三家共用一個攤位號碼，兩家都有「NC Spot Drill」時只比前兩個會併成一個。
 * 🔴 不能只用產品名稱：不同廠商很可能取一樣的名字，
 * 只比名稱會把兩家不同公司的產品併成一個。
 */

import splitMultiValue from './splitMultiValue';

/**
 * 同一個產品的判斷依據。
 *
 * 🔴 接縫要用 \u0000，不要用空白或短橫線。
 * 那些字元本來就可能出現在產品名稱或攤位號碼裡，
 * 於是「A B」＋「C」會跟「A」＋「B C」算成同一個產品而被併掉 ——
 * 而且併掉之後畫面上完全看不出少了一筆。
 */
const identityOf = (product) => `${product['產品名稱'] || ''}\u0000${product['攤位號碼'] || ''}\u0000${product['廠商名稱'] || ''}`;

/**
 * 一列產品資料展開成幾條分類路徑。
 *
 * 大分類那一格可以填多個值（分號隔開），跟廠商的「區域」同一個慣例 ——
 * 客戶 2026-09-14 提供的 TMTS 資料裡已經有 36 筆是這樣填的。
 *
 * 🔴 **只有在中分類與小分類都是空的時候才拆。**
 * 三格都能填多值的話會產生答不出來的歧義：大分類 A;B 配中分類 X;Y，
 * 到底是 A→X 與 B→Y 兩條路徑，還是四種組合？資料本身沒有這個資訊。
 * 猜錯不會報錯，只會安靜地把產品掛到錯的分類底下。
 * 所以中或小分類只要有填，就維持原樣當成一條路徑，不拆。
 *
 * 實際數過五份資料（2024 舊資料、2025 EMO、AMB 2026、TMTS 2026 中英），
 * 中分類與小分類**沒有一筆有值**，所以「不拆」這條路目前不會發生；
 * 它存在是為了哪天客戶真的開始用那兩欄的時候不要安靜地出錯。
 */
const pathsOf = (product) => {
	const 大 = (product['產品大分類'] || '').trim();
	const 中 = (product['產品中分類'] || '').trim();
	const 小 = (product['產品小分類'] || '').trim();

	if (!中 && !小) {
		const 多 = splitMultiValue(大);
		if (多.length > 1) {
			return 多.map((值) => ({ '產品大分類': 值, '產品中分類': '', '產品小分類': '' }));
		}
	}

	return [{ '產品大分類': 大, '產品中分類': 中, '產品小分類': 小 }];
};

const samePath = (a, b) =>
	a['產品大分類'] === b['產品大分類'] &&
	a['產品中分類'] === b['產品中分類'] &&
	a['產品小分類'] === b['產品小分類'];

/**
 * 一個產品掛了哪幾條分類路徑。
 *
 * 併過的產品讀 categoryPaths；沒併過的（或從別的地方來的）就回它自己那一條，
 * 所以比對分類的地方一律呼叫這支就好，不必分辨手上這筆有沒有併過。
 */
export const productCategoryPaths = (product) => {
	if (!product) return [];
	if (Array.isArray(product.categoryPaths) && product.categoryPaths.length) return product.categoryPaths;
	return pathsOf(product);
};

const mergeDuplicateProducts = (products) => {
	if (!Array.isArray(products)) return [];

	const merged = [];
	const seen = new Map();

	products.forEach((product) => {
		const key = identityOf(product);
		const existing = seen.get(key);

		const 這列的路徑 = pathsOf(product);

		if (!existing) {
			const copy = { ...product, categoryPaths: 這列的路徑 };
			seen.set(key, copy);
			merged.push(copy);
			return;
		}

		// 已經有這個產品了：只把它這一列的分類路徑補進去，其餘欄位維持第一列的。
		// 產品圖與產品說明在重複的那幾列通常是一樣的，就算不一樣也該以第一列為準，
		// 不然畫面會隨著分類選了哪一個而換圖，看起來像資料在跳。
		這列的路徑.forEach((path) => {
			if (!existing.categoryPaths.some((known) => samePath(known, path))) {
				existing.categoryPaths.push(path);
			}
		});
	});

	return merged;
};

export default mergeDuplicateProducts;
